import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { HelmetProvider } from 'react-helmet-async';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import ErrorBoundary from "@/components/ErrorBoundary";

// Public pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import ConfirmSignup from "./pages/ConfirmSignup";
import VerifyAccountDeletion from "./pages/VerifyAccountDeletion";
import Sitemap from "./pages/Sitemap";
import FormView from "./pages/FormView";
import Unsubscribe from "./pages/Unsubscribe";

// Auth pages (split-screen layout)
import AuthLogin from "./pages/auth/Login";
import AuthSignup from "./pages/auth/Signup";
import AuthForgotPassword from "./pages/auth/ForgotPassword";

// Protected pages
import Dashboard from "./pages/Dashboard";
import MyForms from "./pages/MyForms";
import FormBuilder from "./pages/FormBuilder";
import Submissions from "./pages/Submissions";
import SubmissionDetail from "./pages/SubmissionDetail";
import Billing from "./pages/Billing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Settings from "./pages/Settings";
import NotificationSettings from "./pages/NotificationSettings";
import CRM from "./pages/CRM";
import Integrations from "./pages/Integrations";
// Contacts merged into CRM
import API from "./pages/API";
import APIDocumentation from "./pages/APIDocumentation";
import Analytics from "./pages/Analytics";
import AIBuilder from "./pages/AIBuilder";
import Blog from "./pages/Blog";
import Dataroom from "./pages/Dataroom";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import NotFound from "./pages/NotFound";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AuthTester from "@/components/AuthTester";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <VercelAnalytics />
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />

                {/* Auth routes — split-screen layout */}
                <Route path="/auth/login" element={<AuthLogin />} />
                <Route path="/auth/signup" element={<AuthSignup />} />
                <Route path="/auth/forgot-password" element={<AuthForgotPassword />} />

                {/* Legacy redirects — keep old URLs working */}
                <Route path="/login" element={<Navigate to="/auth/login" replace />} />
                <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />
                <Route path="/reset-password" element={<Navigate to="/auth/forgot-password" replace />} />

                <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
                <Route path="/confirm-signup" element={<ConfirmSignup />} />
                <Route path="/verify-account-deletion" element={<VerifyAccountDeletion />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/api-docs" element={<APIDocumentation />} />
                <Route path="/test-auth" element={<AuthTester />} />
                <Route path="/form/:formId" element={<FormView />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                {/* Standalone admin login — not linked anywhere in the UI */}
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/f/:formId" element={<FormView />} />
                
                {/* Protected routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/forms" element={<ProtectedRoute><MyForms /></ProtectedRoute>} />
                <Route path="/form-builder/:formId?" element={<ProtectedRoute><FormBuilder /> </ProtectedRoute>} />
                <Route path="/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
                <Route path="/forms/:formId/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
                <Route path="/submissions/:submissionId" element={<ProtectedRoute><SubmissionDetail /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                <Route path="/billing-new" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/contacts" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
                <Route path="/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
                <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                <Route path="/api" element={<ProtectedRoute><API /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                <Route path="/ai-builder" element={<ProtectedRoute><AIBuilder /></ProtectedRoute>} />
                <Route path="/dataroom" element={<ProtectedRoute><Dataroom /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ThemeProvider>
</ErrorBoundary>
);

export default App;
